import React, { useEffect, useState } from 'react';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { EsSignupForm, SignUpdata } from '../EsSignupForm/EsSignupForm';
import { EsLoginForm } from '../EsLoginForm/EsLoginForm';
import { TUsernamePassword } from '../EsLoginForm/EsSigninData';
import { useAuth } from '../../module/AuthenticationModule';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * Authentication Modal component
 * Provides a modal interface for login and signup functionality
 */
export const AuthModal: React.FC = () => {
    const { themeStyle } = useThemeContext();
    const {
        modalState,
        closeAuthModal,
        handleLogin,
        handleSignup,
        handleGetOTP,
        handleResendLoginOtp,
        isAuthLoading,
        isOtpLoading,
        isResendingOtp,
        loginMessage,
        loginChannel,
        audmeData,
        nextStep,
        openAuthModal,
        handleCheckUserAvailability,
        handleUsernameCheck,
        updateUserDetails,
    } = useAuth();

    const { isOpen, mode, step } = modalState;
    const { device } = useThemeContext();
    const [animate, setAnimate] = useState(false);

    // We'll track the current signup step separately to avoid animation resets
    const [signupStep, setSignupStep] = useState(0);

    // Update the signupStep when modal step changes and we're in signup mode
    useEffect(() => {
        if (mode === 'signup' && step > signupStep) {
            setSignupStep(step);
        }
    }, [mode, step, signupStep]);

    // Set animation state after a short delay
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                setAnimate(true);
            }, 50);
            return () => clearTimeout(timer);
        } else {
            setAnimate(false);
        }
    }, [isOpen]);

    // Handle login action
    const onLogin = (data: TUsernamePassword, callback?: () => void, isLastStep?: boolean) => {
        handleLogin(data, callback || nextStep, isLastStep);
    };

    // Handle signup action
    const onSignup = (data: SignUpdata, code?: string[], callback?: () => void) => {
        handleSignup(data, code, callback);
    };

    // Get OTP for signup
    const onGetOTP = (data: SignUpdata, callback: () => void, resend?: boolean) => {
        // For initial OTP request (not resend), update our signupStep to 3 (verification step)
        if (!resend) {
            setSignupStep(3);
        }
        return handleGetOTP(data, callback, resend);
    };

    // Resend login OTP
    const onResendLoginOtp = (data: TUsernamePassword) => {
        return handleResendLoginOtp(data);
    };

    // Create animation key based on mode, but for signup use our stable signupStep
    // This prevents re-renders when the modal step changes for signup flow
    const animationKey = mode === 'login' ? `${mode}-${step}` : `${mode}-stable`;

    return (
        <Modal
            open={isOpen}
            onClose={closeAuthModal}
            aria-labelledby="auth-modal-title"
            aria-describedby="auth-modal-description"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(5px)',
            }}>
            <Box
                sx={{
                    position: 'relative',
                    width: { xs: 'calc(100% - 32px)', sm: '100%' },
                    maxWidth: {
                        xs: 'calc(100% - 32px)',
                        sm: 'fit-content',
                    },
                    maxHeight: device.isMobile ? '100vh' : '90vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    outline: 'none',
                    borderRadius: '20px',
                    mx: { xs: 2, sm: 0 },
                    px: { xs: 0, sm: 1 },
                    boxSizing: 'border-box',
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                <IconButton
                    onClick={closeAuthModal}
                    sx={{
                        position: 'absolute',
                        right: { xs: 12, sm: 20 },
                        top: { xs: 12, sm: 20 },
                        color: theme[themeStyle].primary_white,
                        zIndex: 10,
                    }}>
                    <CloseIcon />
                </IconButton>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={animationKey}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: animate ? 1 : 0, y: animate ? 0 : 10 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                        {mode === 'login' ? (
                            <EsLoginForm
                                onSubmit={onLogin}
                                onResendOtp={onResendLoginOtp}
                                loading={isAuthLoading}
                                otpLoading={isOtpLoading}
                                resendInProgress={isResendingOtp}
                                loginMessage={loginMessage}
                                loginChannel={loginChannel}
                                audmeData={audmeData}
                                isModalMode
                                onSignUpClick={() => openAuthModal('signup')}
                            />
                        ) : (
                            <EsSignupForm
                                onSubmitSignUpOTP={onSignup}
                                getSignUpOTP={onGetOTP}
                                checkUserDataAvailability={handleCheckUserAvailability}
                                userNameAvailable={handleUsernameCheck}
                                loading={isAuthLoading}
                                otpLoading={isOtpLoading}
                                resendInProgress={isResendingOtp}
                                updateUserDetails={updateUserDetails}
                                onSignInClick={() => openAuthModal('login')}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>
            </Box>
        </Modal>
    );
};
